import { Button, Select, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import {
  MAX_OUTBOUND_RECIPIENTS,
  isLikelyEmailAddress,
} from '../../lib/shared/chatwootRecipients'
import { useChatwootContacts } from './hooks/useChatwootContacts'
import type { ChatwootContactsResponse } from './types'
import styles from './ChatwootInbox.module.less'

export interface ReplyRecipients {
  to: string[]
  cc: string[]
  bcc: string[]
}

interface ChatwootReplyRecipientsProps {
  value: ReplyRecipients
  onChange: (next: ReplyRecipients) => void
  disabled?: boolean
  // True when To is empty, which blocks the send: Chatwoot would otherwise fall
  // back to the conversation's contact and mail the person just removed.
  missingRecipient?: boolean
  // False where a contact picker already chose the recipient, so a To row here
  // would contradict it.
  showTo?: boolean
  companyUid?: string | null
}

// Editable recipients for an email reply.
//
// Chatwoot addresses a reply to the conversation's contact, which is wrong the
// moment a mail was forwarded: the contact is then the colleague who forwarded
// it, not the client owed the answer.
//
// Tag selects rather than plain inputs, so each address is its own removable
// chip — removing one is awkward in a comma-separated text field.
export function ChatwootReplyRecipients({
  value,
  onChange,
  disabled = false,
  missingRecipient = false,
  showTo = true,
  companyUid,
}: ChatwootReplyRecipientsProps) {
  const { t } = useTranslation()
  // Cc and Bcc stay folded away until they are needed — most replies use
  // neither, and three rows above the field crowds out the message itself.
  const [expanded, setExpanded] = useState(
    () => value.cc.length > 0 || value.bcc.length > 0
  )

  // The same server-side contact search the new-conversation picker uses, so
  // known contacts are offered here too instead of demanding the full address
  // from memory. One shared query across the rows: the operator types in one
  // field at a time. Debounced like the other pickers so a callable isn't
  // fired on every keystroke. Free typing stays possible (tags mode) — a third
  // party who is no contact must still be addressable.
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [searchInput])
  const { data: contactsData } = useChatwootContacts(search, 1, companyUid)

  const contactOptions = useMemo(() => {
    const response = contactsData as ChatwootContactsResponse | undefined
    const contacts = response?.contacts || response?.result?.contacts || []
    const seen = new Set<string>()
    const options: { value: string; label: string }[] = []
    for (const contact of contacts) {
      const email = contact.email?.trim()
      if (!email) {
        continue
      }
      const key = email.toLowerCase()
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      const name = contact.name?.trim()
      options.push({
        value: email,
        label: name && name !== email ? `${name} <${email}>` : email,
      })
    }
    return options
  }, [contactsData])

  const invalid = useMemo(
    () =>
      [...value.to, ...value.cc, ...value.bcc].filter(
        (address) => !isLikelyEmailAddress(address)
      ),
    [value]
  )

  function row(
    field: keyof ReplyRecipients,
    label: string,
    placeholder?: string
  ) {
    return (
      <div className={styles.composerRecipientRow}>
        <Typography.Text
          type="secondary"
          className={styles.composerRecipientLabel}
        >
          {label}
        </Typography.Text>
        <Select
          mode="tags"
          className={styles.composerRecipientSelect}
          value={value[field]}
          onChange={(next: string[]) =>
            onChange({
              ...value,
              [field]: next.slice(0, MAX_OUTBOUND_RECIPIENTS),
            })
          }
          disabled={disabled}
          placeholder={placeholder}
          aria-label={label}
          options={contactOptions}
          onSearch={setSearchInput}
          // The search is server-side; local filtering would hide fresh results
          // while the debounced refetch is still in flight.
          filterOption={false}
          notFoundContent={null}
          suffixIcon={null}
          tokenSeparators={[',', ';', ' ']}
          variant="borderless"
          size="small"
        />
      </div>
    )
  }

  return (
    <div className={styles.composerRecipients}>
      {showTo &&
        row(
          'to',
          t('frontend:chatwoot.email.to'),
          t('frontend:chatwoot.email.recipientPlaceholder')
        )}
      {expanded && row('cc', t('frontend:chatwoot.email.cc'))}
      {expanded && row('bcc', t('frontend:chatwoot.email.bcc'))}
      {!expanded && (
        <Button
          type="link"
          size="small"
          disabled={disabled}
          onClick={() => setExpanded(true)}
          className={styles.composerRecipientToggle}
        >
          {t('frontend:chatwoot.email.addCc')}
        </Button>
      )}
      {missingRecipient && (
        <Typography.Text
          type="danger"
          className={styles.composerRecipientError}
        >
          {t('frontend:chatwoot.email.recipientRequired')}
        </Typography.Text>
      )}
      {invalid.length > 0 && (
        <Tooltip title={invalid.join(', ')}>
          <Typography.Text
            type="danger"
            className={styles.composerRecipientError}
          >
            {t('frontend:chatwoot.email.invalidRecipient', {
              count: invalid.length,
            })}
          </Typography.Text>
        </Tooltip>
      )}
    </div>
  )
}
